/**
 * Render entrypoint.
 *
 * Order matters: `<StrictMode>` -> `<BrowserRouter>` -> `<I18nProvider>`
 * -> `<TenantConfigProvider>` so router context is available everywhere
 * and the tenant provider's error boundary wraps the rest of the tree.
 *
 * Env validation runs first via a dynamic import. If `env.ts` throws an
 * `EnvValidationError`, we render the boot fallback instead of dying
 * with a blank page.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/global.css';

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

const root = createRoot(rootEl);

void (async (): Promise<void> => {
  try {
    // Phase 5B — `/preview/layout` short-circuit.
    //
    // The admin embeds this surface in an iframe to show the operator
    // a LayoutPlan rendered with real primitives. The plan arrives in
    // the URL (base64-encoded JSON) so the render layer needs no
    // tenant config, no engine fetch, no router, no i18n — just the
    // primitive registry and the inline Zod schema. We intercept BEFORE
    // env validation and tenant boot so a misconfigured env / missing
    // tenant config can't blank the preview pane.
    //
    // nginx's `^~ /preview/` block (see nginx.conf.template) relaxes
    // frame-ancestors to admin's hostname; everything else stays locked.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === '/preview/layout'
    ) {
      const { LayoutPreview } = await import('./components/preview/LayoutPreview.js');
      root.render(
        <StrictMode>
          <LayoutPreview />
        </StrictMode>,
      );
      return;
    }

    // `/preview/avatar-story` short-circuit — the admin /variants showroom
    // iframes this to show the avatar Story WITH overlays (odds + widgets over
    // the video) + a brand logo, as the external-channel version. It needs the
    // tenant + engine providers (to fetch the video) but NOT the site chrome
    // (AppShell / router routes), so we render the providers around the preview
    // component directly. On-site Stories are unaffected. CSP is relaxed for
    // iframing by nginx's `^~ /preview/` block.
    if (
      typeof window !== 'undefined' &&
      window.location.pathname === '/preview/avatar-story'
    ) {
      await import('./env.js');
      const [
        { AvatarStoryPreview },
        { BrowserRouter },
        { TenantConfigProvider },
        { I18nProvider },
        { EngineClientProvider },
      ] = await Promise.all([
        import('./components/preview/AvatarStoryPreview.js'),
        import('react-router-dom'),
        import('./tenant/TenantConfigProvider.js'),
        import('./i18n/I18nProvider.js'),
        import('./api/EngineClientProvider.js'),
      ]);
      root.render(
        <StrictMode>
          <BrowserRouter>
            <I18nProvider>
              <TenantConfigProvider>
                <EngineClientProvider>
                  <AvatarStoryPreview />
                </EngineClientProvider>
              </TenantConfigProvider>
            </I18nProvider>
          </BrowserRouter>
        </StrictMode>,
      );
      return;
    }

    // Validate env first. Failure throws synchronously inside env.ts.
    await import('./env.js');

    const [
      { App },
      { BrowserRouter },
      { TenantConfigProvider },
      { I18nProvider },
      { EngineClientProvider },
    ] = await Promise.all([
      import('./App.js'),
      import('react-router-dom'),
      import('./tenant/TenantConfigProvider.js'),
      import('./i18n/I18nProvider.js'),
      import('./api/EngineClientProvider.js'),
    ]);

    // Provider order — important.
    //
    // `<I18nProvider>` is mounted OUTSIDE `<TenantConfigProvider>` so the
    // tenant boot states (loading / error / paused) can call
    // `useTranslation()` without crashing. The trade-off: i18next is
    // initialised with the bare default bundle until tenant config
    // arrives and `<TenantConfigProvider>` runs `initI18n(locale)`.
    //
    // Practical impact: any string rendered by a boot fallback (e.g.
    // `<TenantBootFallback>`, `<ErrorBootState>`) resolves via i18next's
    // `defaultValue` until tenant locale bundles load. Translators do
    // not have to localise those strings — they're documented English
    // fallbacks. Once the tenant config resolves, i18next switches to
    // the tenant locale and the rest of the app (chrome, widgets, etc.)
    // gets full translations.
    root.render(
      <StrictMode>
        <BrowserRouter>
          <I18nProvider>
            <TenantConfigProvider>
              <EngineClientProvider>
                <App />
              </EngineClientProvider>
            </TenantConfigProvider>
          </I18nProvider>
        </BrowserRouter>
      </StrictMode>,
    );
  } catch (err) {
    const { ErrorBootState } = await import('./components/system/BootStates.js');
    const error = err instanceof Error ? err : new Error('Unknown boot error');
    root.render(
      <StrictMode>
        <ErrorBootState error={error} />
      </StrictMode>,
    );
  }
})();
